import type { Drawing, Point, Stroke } from '../../shared/art/types';

export const LIVE_PROTOCOL_VERSION = 3;
export const LIVE_CAPACITY = 10;
export const LIVE_REACTION_COOLDOWN = 200;
export const LIVE_REACTIONS = ['rose', 'egg', 'like', 'dislike'] as const;
export type LiveReactionKind = typeof LIVE_REACTIONS[number];
export interface LiveBubble { text?: string; correct: boolean; revision: number; score?: number }
export interface LiveMatchResult { userId: string; nickname: string; score: number; eligible: boolean; rank?: number; expectedStars: number; stars: number }
export interface LiveMatchSummary { id: string; completed: boolean; reason: string; results: LiveMatchResult[]; settledAt: number }
export interface LiveReaction { id: string; matchId: string; roundId?: string; userId: string; targetUserId: string; kind: LiveReactionKind; createdAt: number }
export const LIVE_DURATIONS = [60, 120, 240, 480] as const;
export const LIVE_FEEDBACK = ['画得真好', '我有思路了', '再来一点提示', '好难猜呀', '加油友友', '太有默契了'] as const;
export type LiveDuration = typeof LIVE_DURATIONS[number];
export type LiveAnswerMode = 'candidates' | 'text' | 'both';
export type LivePhase = 'waiting' | 'choosing' | 'drawing' | 'revealing';
export interface LiveRoomConfig { name: string; duration: LiveDuration; answerMode: LiveAnswerMode; isPublic: boolean }
export interface LiveRoomSummary extends LiveRoomConfig {
  id: string; code: string; mode: 'live-draw'; ownerId: string; capacity: 10;
  memberCount: number; phase: LivePhase; createdAt: number; updatedAt: number;
}
export interface LiveMember {
  id: string; nickname: string; avatarUrl?: string; score: number; stars: number;
  connected: boolean; spectator: boolean; guessed: boolean;
  bubble?: LiveBubble;
}
export interface LiveChoice { id: string; text: string; category: string }
export interface LiveRoundResult { userId: string; score: number; stars: number; correct: boolean }
export interface LiveRoundView {
  id: string; number: number; artistId: string; phase: Exclude<LivePhase, 'waiting'>;
  deadlineAt: number; seq: number; drawing: Drawing; category?: string; letterCount?: number;
  startedAt?: number; acceleration?: 0 | 1 | 2; guesserCount?: number;
  /** Artist-only history availability; snapshots never include previous drawings. */
  history?: { undo: number; redo: number };
  choices?: LiveChoice[]; word?: string; banks?: string[][]; results?: LiveRoundResult[];
  artworkId?: string; endReason?: string; refreshes?: number;
}
export interface LiveRoomView extends LiveRoomSummary {
  members: LiveMember[]; round?: LiveRoundView; role: 'artist' | 'guesser' | 'spectator';
  revision: number; serverTime: number; dailyStarsRemaining: number;
  match?: { id: string; current: number; total: number; waitingForPlayers: boolean };
  lastMatch?: LiveMatchSummary;
  feedback?: { userId: string; text: string; createdAt: number }[];
}
/** Per-guess update, deliberately excludes canvas, history, candidates and the answer. */
export interface LiveProgress {
  roomId: string; matchId: string; roundId: string; revision: number; serverTime: number;
  deadlineAt: number; acceleration: 0 | 1 | 2; category?: string;
  members: Pick<LiveMember, 'id' | 'score' | 'guessed' | 'bubble'>[];
}
export type LiveDrawingOperation =
  | { kind: 'begin'; stroke: Stroke }
  | { kind: 'append'; points: Point[] }
  | { kind: 'end' }
  | { kind: 'undo' | 'redo' | 'clear' }
  | { kind: 'fill'; background: string };
export interface LiveDrawingCommand { roundId: string; seq: number; operation: LiveDrawingOperation }
export interface LiveGuessCommand { roundId: string; answer?: string; indices?: number[] }
export interface LiveGuessResult { correct: boolean; alreadyGuessed: boolean; score: number; stars: number }
export interface LiveArtworkSummary {
  mode: 'live-draw'; id: string; roomId: string; roomName: string; artistId: string; artistName: string;
  number: number; word: string; createdAt: number; likeCount: number; likedByMe: boolean;
  completed: boolean; endReason: string; participantCount: number;
}
export interface LiveArtwork extends LiveArtworkSummary { drawing: Drawing; results: LiveRoundResult[] }
export type LiveClientMessage =
  | { type: 'auth'; token: string; roomId: string; version: 3; capabilities?: ('progress')[] }
  | { type: 'ping'; id?: string }
  | { type: 'sync' }
  | { type: 'drawing'; id: string; command: LiveDrawingCommand }
  | { type: 'guess'; id: string; command: LiveGuessCommand }
  | { type: 'choose'; id: string; roundId: string; wordId: string };
export type LiveServerMessage =
  | { type: 'state'; room: LiveRoomView }
  | { type: 'progress'; progress: LiveProgress }
  | { type: 'drawing'; command: LiveDrawingCommand }
  | { type: 'ack'; id: string; seq?: number; result?: LiveGuessResult }
  | { type: 'pong'; id?: string; serverTime: number }
  | { type: 'error'; id?: string; status: number; message: string }
  | { type: 'feedback'; userId: string; nickname: string; text: string }
  | { type: 'bubble'; matchId: string; roundId: string; userId: string; bubble: LiveBubble }
  | { type: 'reaction'; reaction: LiveReaction }
  | { type: 'removed'; reason: string }
  | { type: 'closed'; reason: string };
